import * as React from 'react';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { getTagColorClasses } from '@/lib/tagColors';
import { EditableAmount } from './EditableAmount';
import { useUpdateTransaction } from '@/hooks/useTransactions';
import { TransactionService } from '@/services/TransactionService';
import type { Transaction } from '@/types';
import { toast } from 'sonner';

interface TransactionItemProps {
  transaction: Transaction;
  onClick?: () => void;
  currentBudgetId?: string;
  editable?: boolean;
  draggable?: boolean;
  onContextMenuRequest?: (x: number, y: number) => void;
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
}

export function TransactionItem({
  transaction,
  onClick,
  currentBudgetId,
  editable = false,
  draggable: isDraggable = false,
  onContextMenuRequest,
  selectionMode = false,
  isSelected = false,
  onToggleSelect,
}: TransactionItemProps) {
  const updateTransaction = useUpdateTransaction();
  const showConversion = transaction.requiresFxConversion && transaction.convertedAmount;
  const showBudgetBadge = currentBudgetId && transaction.budgetId !== currentBudgetId && transaction.budgetName;
  const isIncome = transaction.amount < 0;
  const allowEdit = editable && !selectionMode;
  const allowDrag = isDraggable && !selectionMode;

  const longPressTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggered = React.useRef(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!onContextMenuRequest || selectionMode) return;
    const touch = e.touches[0];
    if (!touch) return;
    const x = touch.clientX;
    const y = touch.clientY;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      onContextMenuRequest(x, y);
    }, 500);
  };

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    if (!onContextMenuRequest || selectionMode) return;
    e.preventDefault();
    onContextMenuRequest(e.clientX, e.clientY);
  };

  const handleClick = () => {
    if (longPressTriggered.current) {
      longPressTriggered.current = false;
      return;
    }
    if (selectionMode) {
      onToggleSelect?.();
      return;
    }
    onClick?.();
  };

  const handleAmountUpdate = async (newAmount: number) => {
    try {
      let version = transaction.version;
      if (version === undefined) {
        const full = await TransactionService.getById(transaction.id);
        version = full?.version;
      }
      if (version === undefined) {
        throw new Error('Could not load transaction version');
      }

      await updateTransaction.mutateAsync({
        id: transaction.id,
        request: {
          amount: newAmount,
          currency: transaction.currency,
          date: transaction.date,
          note: transaction.note ?? undefined,
          merchant: transaction.merchant ?? undefined,
          category: transaction.category ?? undefined,
          version,
          budgetId: transaction.budgetId,
        },
      });
      toast.success('Amount updated successfully');
    } catch (error: any) {
      toast.error(error?.message ?? 'Failed to update amount');
      throw error;
    }
  };

  const isInteractive = selectionMode || !!onClick;
  const category = transaction.category?.trim() || null;
  const tags = transaction.tags ?? [];
  const hasTags = tags.length > 0;
  const showDesktopMeta = Boolean(category || showBudgetBadge);

  return (
    <div
      data-testid={`transaction-item-${transaction.id}`}
      aria-selected={selectionMode ? isSelected : undefined}
      className={cn(
        'p-3 rounded-lg min-w-0 bg-card hover:bg-accent/50 transition-colors',
        'flex items-start justify-between gap-3',
        selectionMode
          ? 'md:grid md:grid-cols-[1.5rem_6.5rem_minmax(0,1fr)_7.5rem] md:items-start md:gap-3'
          : 'md:grid md:grid-cols-[6.5rem_minmax(0,1fr)_7.5rem] md:items-start md:gap-3',
        isInteractive && 'cursor-pointer active:scale-[0.99] md:active:scale-100',
        allowDrag && 'cursor-grab active:cursor-grabbing',
        selectionMode && isSelected && 'ring-2 ring-primary/40 bg-primary/5'
      )}
      title={transaction.note ?? undefined}
      draggable={allowDrag}
      onDragStart={
        allowDrag
          ? (e) => {
              e.dataTransfer.setData('application/x-transaction-id', transaction.id);
              e.dataTransfer.effectAllowed = 'move';
              setTimeout(() => {
                (e.target as HTMLElement).style.opacity = '0.4';
              }, 0);
            }
          : undefined
      }
      onDragEnd={
        allowDrag
          ? (e) => {
              (e.target as HTMLElement).style.opacity = '';
            }
          : undefined
      }
      onClick={isInteractive ? handleClick : undefined}
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchEnd={clearLongPress}
      onTouchMove={clearLongPress}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
    >
      {selectionMode && (
        <span
          className={cn(
            'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border',
            isSelected
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-input bg-background'
          )}
          aria-hidden="true"
        >
          {isSelected && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          )}
        </span>
      )}

      <span className="hidden md:block text-sm text-muted-foreground tabular-nums whitespace-nowrap pt-0.5">
        {formatDate(transaction.date)}
      </span>

      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 min-w-0">
          {transaction.merchant ? (
            <span className="font-medium text-foreground truncate">{transaction.merchant}</span>
          ) : (
            <span className="text-muted-foreground italic truncate">No merchant</span>
          )}
          {isIncome && (
            <span className="inline-flex h-5 items-center rounded-full bg-green-100 px-2 text-xs font-medium text-green-700 dark:bg-green-900 dark:text-green-300 whitespace-nowrap shrink-0">
              Income
            </span>
          )}
          {transaction.isHistorical && (
            <span className="inline-flex h-5 items-center rounded-full bg-amber-100 px-1.5 text-xs text-amber-700 dark:bg-amber-900 dark:text-amber-300 shrink-0">
              Historical
            </span>
          )}
        </div>

        {/* Mobile meta: date + optional category/budget/note */}
        <div className="md:hidden flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-muted-foreground">
          <span className="tabular-nums">{formatDate(transaction.date)}</span>
          {category && (
            <>
              <span aria-hidden="true">·</span>
              <span className="truncate">{category}</span>
            </>
          )}
          {showBudgetBadge && (
            <>
              <span aria-hidden="true">·</span>
              <span className="truncate text-blue-700 dark:text-blue-300">
                {transaction.budgetName}
              </span>
            </>
          )}
          {transaction.note && (
            <span className="w-full truncate">{transaction.note}</span>
          )}
        </div>

        {/* Desktop meta: only when category or foreign budget adds info */}
        {showDesktopMeta && (
          <div className="hidden md:flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-muted-foreground">
            {category && <span className="truncate">{category}</span>}
            {category && showBudgetBadge && <span aria-hidden="true">·</span>}
            {showBudgetBadge && (
              <span className="truncate text-blue-700 dark:text-blue-300">
                {transaction.budgetName}
              </span>
            )}
          </div>
        )}

        {hasTags && (
          <div className="flex flex-wrap gap-1">
            {tags.map((tag) => {
              const palette = getTagColorClasses(tag.id || tag.name);
              return (
                <span
                  key={tag.id}
                  className={`inline-flex max-w-full items-center truncate rounded-full border px-2 py-0.5 text-[11px] ${palette.solid}`}
                >
                  {tag.name}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="text-right shrink-0 pt-0.5">
        {allowEdit ? (
          <EditableAmount
            amount={transaction.amount}
            currency={transaction.currency}
            onSave={handleAmountUpdate}
            disabled={updateTransaction.isPending}
          />
        ) : (
          <div className="font-medium text-foreground tabular-nums">
            {formatCurrency(transaction.amount, transaction.currency)}
          </div>
        )}
        {showConversion && (
          <div className="text-xs text-muted-foreground">
            ≈ {formatCurrency(transaction.convertedAmount!, transaction.currency)}
            {transaction.fxRateUsed && (
              <span className="ml-1">@ {transaction.fxRateUsed.toFixed(4)}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface TransactionListProps {
  transactions: Transaction[];
  onTransactionClick?: (transaction: Transaction) => void;
  grouped?: boolean;
  currentBudgetId?: string;
  editable?: boolean;
  draggable?: boolean;
  onTransactionContextMenu?: (transaction: Transaction, x: number, y: number) => void;
  selectionMode?: boolean;
  selectedIds?: string[];
  onToggleSelect?: (transactionId: string) => void;
}

export function TransactionList({
  transactions,
  onTransactionClick,
  grouped = false,
  currentBudgetId,
  editable = false,
  draggable = false,
  onTransactionContextMenu,
  selectionMode = false,
  selectedIds = [],
  onToggleSelect,
}: TransactionListProps) {
  if (transactions.length === 0) {
    return null;
  }

  if (!grouped) {
    // show newest first
    const sorted = [...transactions].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    return (
      <div className="space-y-2">
        {sorted.map((transaction) => (
          <TransactionItem
            key={transaction.id}
            transaction={transaction}
            onClick={onTransactionClick ? () => onTransactionClick(transaction) : undefined}
            currentBudgetId={currentBudgetId}
            editable={editable}
            draggable={draggable}
            onContextMenuRequest={
              onTransactionContextMenu
                ? (x, y) => onTransactionContextMenu(transaction, x, y)
                : undefined
            }
            selectionMode={selectionMode}
            isSelected={selectedIds.includes(transaction.id)}
            onToggleSelect={onToggleSelect ? () => onToggleSelect(transaction.id) : undefined}
          />
        ))}
      </div>
    );
  }

  const toDateOnly = (value: string) => value.split('T')[0];
  const addDaysUtc = (date: string, days: number) => {
    const [year, month, day] = date.split('-').map(Number);
    const utc = Date.UTC(year, (month ?? 1) - 1, day ?? 1);
    const next = new Date(utc + days * 24 * 60 * 60 * 1000);
    return next.toISOString().split('T')[0];
  };

  // Group by date (normalized) — only used when grouped=true
  const groups = React.useMemo(() => {
    const result = new Map<string, Transaction[]>();
    for (const transaction of transactions) {
      const date = toDateOnly(transaction.date);
      const existing = result.get(date) ?? [];
      existing.push(transaction);
      result.set(date, existing);
    }
    // ensure each day's list is sorted newest-first
    result.forEach((arr, key) => {
      arr.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      result.set(key, arr);
    });
    return result;
  }, [transactions]);

  const sortedDates = React.useMemo(() => {
    return Array.from(groups.keys()).sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime()
    );
  }, [groups]);

  const ranges = React.useMemo(() => {
    const result: { start: string; end: string; dates: string[] }[] = [];
    for (const date of sortedDates) {
      const last = result[result.length - 1];
      if (!last) {
        result.push({ start: date, end: date, dates: [date] });
        continue;
      }
      const isConsecutive = addDaysUtc(last.end, -1) === date;
      if (isConsecutive) {
        last.end = date;
        last.dates.push(date);
      } else {
        result.push({ start: date, end: date, dates: [date] });
      }
    }
    return result;
  }, [sortedDates]);

  const [expandedRanges, setExpandedRanges] = React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    setExpandedRanges((prev) => {
      const next = { ...prev };
      let hasChanges = false;
      ranges.forEach((range) => {
        const key = `${range.start}_${range.end}`;
        if (next[key] === undefined) {
          next[key] = false;
          hasChanges = true;
        }
      });
      return hasChanges ? next : prev;
    });
  }, [ranges]);

  const addToTotals = (totals: Map<string, number>, transaction: Transaction) => {
    const currency = transaction.currency;
    totals.set(currency, (totals.get(currency) ?? 0) + transaction.amount);
  };

  const formatTotals = (totals: Map<string, number>) =>
    Array.from(totals.entries())
      .map(([currency, total]) => formatCurrency(total, currency))
      .join(' • ');

  const formatRangeTotal = (range: { dates: string[] }) => {
    const totals = new Map<string, number>();
    range.dates.forEach((date) => {
      const dateTransactions = groups.get(date) ?? [];
      dateTransactions.forEach((transaction) => addToTotals(totals, transaction));
    });

    return formatTotals(totals);
  };

  const getRangeTags = (range: { dates: string[] }) => {
    const unique = new Map<string, string>();

    range.dates.forEach((date) => {
      const dateTransactions = groups.get(date) ?? [];
      dateTransactions.forEach((transaction) => {
        (transaction.tags ?? []).forEach((tag) => {
          unique.set(tag.id, tag.name);
        });
      });
    });

    return Array.from(unique.entries()).map(([id, name]) => ({ id, name }));
  };

  return (
    <div className="space-y-6">
      {ranges.map((range) => {
        const key = `${range.start}_${range.end}`;
        const isExpanded = expandedRanges[key];
        const rangeTags = getRangeTags(range);
        const label = range.start === range.end
          ? formatDate(range.start)
          : `${formatDate(range.end)} - ${formatDate(range.start)}`;

        return (
          <div key={key} className="space-y-2">
            <button
              type="button"
              onClick={() => setExpandedRanges((prev) => ({ ...prev, [key]: !isExpanded }))}
              className="flex w-full items-center justify-between gap-3 rounded-lg border border-input px-3 py-2 text-left hover:bg-muted"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={cn('transition-transform', isExpanded ? 'rotate-90' : 'rotate-0')}
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                  <span className="text-sm font-medium text-foreground">{label}</span>
                </div>

                {rangeTags.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5 pl-6">
                    {rangeTags.map((tag) => {
                      const palette = getTagColorClasses(tag.id || tag.name);
                      return (
                        <span
                          key={tag.id}
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${palette.subtle}`}
                        >
                          {tag.name}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <span className="text-sm font-medium text-foreground">
                {formatRangeTotal(range)}
              </span>
            </button>

            {isExpanded && (
              <div className="space-y-1">
                {range.dates.flatMap((date) => groups.get(date)!).map((transaction) => (
                  <TransactionItem
                    key={transaction.id}
                    transaction={transaction}
                    onClick={
                      onTransactionClick
                        ? () => onTransactionClick(transaction)
                        : undefined
                    }
                    currentBudgetId={currentBudgetId}
                    editable={editable}
                    draggable={draggable}
                    onContextMenuRequest={
                      onTransactionContextMenu
                        ? (x, y) => onTransactionContextMenu(transaction, x, y)
                        : undefined
                    }
                    selectionMode={selectionMode}
                    isSelected={selectedIds.includes(transaction.id)}
                    onToggleSelect={
                      onToggleSelect ? () => onToggleSelect(transaction.id) : undefined
                    }
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
